import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PhoneCall, Plus, Trash2, RefreshCw, Check, X, Bot, Hash, Globe2 } from "lucide-react";

/**
 * Growth Engine · AI Calling sub-tab (Phase 3).
 *
 * Three sections:
 *   1) Setup checklist — Twilio + ElevenLabs config, number library
 *      per region (buy US / UK / IN via Twilio REST API + auto-import
 *      into ElevenLabs)
 *   2) Agent library — programmable AI SDRs, each grounded in the
 *      Service KB with voice + language + region
 *   3) Call history — every outbound call with outcome + transcript
 *      preview + link back to the prospect
 */
const REGIONS = [
  { key: "US", label: "United States (+1)",   flag: "🇺🇸" },
  { key: "GB", label: "United Kingdom (+44)", flag: "🇬🇧" },
  { key: "IN", label: "India (+91)",          flag: "🇮🇳" },
  { key: "DE", label: "Germany (+49)",        flag: "🇩🇪" },
  { key: "AU", label: "Australia (+61)",      flag: "🇦🇺" },
];

// A small default catalogue of ElevenLabs voice IDs — admins can paste
// their own IDs in the "custom" field when creating an agent.
const DEFAULT_VOICES = [
  { id: "9BWtsMINqrJLrRacOk9x", name: "Aria · EN-US (f, warm) — recommended" },
  { id: "cgSgspJ2msm6clMCkdW9", name: "Jessica · EN-US (f, conversational)" },
  { id: "XB0fDUnXU5powFXDhCwa", name: "Charlotte · EN-GB (f, natural)" },
  { id: "iP95p4xoKVk53GoZ742B", name: "Chris · EN-US (m, friendly)" },
  { id: "onwK4e9ZLuTAKqWW03F9", name: "Daniel · EN-GB (m, warm)" },
  { id: "21m00Tcm4TlvDq8ikWAM", name: "Rachel · EN-US (f, calm)" },
  { id: "TxGEqnHWrfWFTfGW9XjX", name: "Josh · EN-US (m, warm)" },
];

export default function GrowthEngineCalling() {
  const [config, setConfig] = useState(null);
  const [numbers, setNumbers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewAgent, setShowNewAgent] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, n, a, k] = await Promise.all([
        api.get("/admin/growth/voice/config"),
        api.get("/admin/growth/voice/numbers"),
        api.get("/admin/growth/voice/agents"),
        api.get("/admin/growth/voice/calls?limit=25"),
      ]);
      setConfig(c.data);
      setNumbers(n.data.items || []);
      setAgents(a.data.items || []);
      setCalls(k.data.items || []);
    } catch (e) {
      toast.error("Load failed: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const purchase = async (country) => {
    if (!window.confirm(`Buy a voice-enabled Twilio number in ${country}?\nCost: ~$1/month + $0.014-0.02/min outbound.`)) return;
    const t = toast.loading(`Provisioning ${country} number…`);
    try {
      const { data } = await api.post("/admin/growth/voice/numbers/purchase", { country });
      toast.dismiss(t);
      if (data.elevenlabs_error) {
        toast.warning(`Number bought (${data.phone_number}) — but ElevenLabs import failed: ${data.elevenlabs_error}`);
      } else {
        toast.success(`Provisioned ${data.phone_number}`);
      }
      load();
    } catch (e) {
      toast.dismiss(t);
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const delAgent = async (id) => {
    if (!window.confirm("Delete this agent? It will be removed from ElevenLabs too.")) return;
    try {
      await api.delete(`/admin/growth/voice/agents/${id}`);
      toast.success("Agent deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div className="space-y-6" data-testid="ge-calling">
      {/* ── SETUP CHECKLIST ─────────────────────────────────────────── */}
      <section className="bg-white border border-slate-200 rounded-lg p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
            <PhoneCall size={16} /> Setup
          </h3>
          <button onClick={load} className="text-xs px-3 py-1.5 border border-slate-200 rounded hover:bg-slate-50 flex items-center gap-1">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
        {config && (
          <div className="grid grid-cols-2 gap-3 text-sm">
            <SetupRow ok={config.twilio_configured} label="Twilio Account"     detail={config.twilio_account_sid_tail ? `SID ending …${config.twilio_account_sid_tail}` : "Not configured"} />
            <SetupRow ok={config.elevenlabs_configured} label="ElevenLabs API" detail={config.elevenlabs_configured ? "Pro plan connected" : "Not configured"} />
          </div>
        )}

        <div className="pt-3 border-t border-slate-100 space-y-3">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-600">Voice numbers by region</div>
          <div className="flex flex-wrap gap-2">
            {REGIONS.map((r) => {
              const bought = numbers.find((n) => n.country === r.key);
              return (
                <div key={r.key} className={"px-3 py-2 border rounded-lg flex items-center gap-3 " +
                                            (bought ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50")}>
                  <span className="text-lg">{r.flag}</span>
                  <div>
                    <div className="text-xs font-bold text-slate-800">{r.label}</div>
                    {bought ? (
                      <div className="text-[11px] font-mono text-emerald-700 flex items-center gap-1">
                        <Check size={11} /> {bought.phone_number}
                        {bought.status !== "active" && <span className="text-amber-700 ml-1">(EL import pending)</span>}
                      </div>
                    ) : (
                      <button onClick={() => purchase(r.key)} disabled={!config?.twilio_configured}
                              className="text-[11px] text-cyan-700 font-semibold hover:underline disabled:opacity-50"
                              data-testid={`buy-num-${r.key}`}>
                        Buy number →
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-slate-400">
            Non-US numbers may require a Twilio regulatory bundle (KYC). If a purchase errors out, complete the KYC at
            {" "}
            <a href="https://console.twilio.com/us1/develop/phone-numbers/regulatory-compliance"
               target="_blank" rel="noopener noreferrer" className="text-cyan-700 underline">
              console.twilio.com/regulatory-compliance
            </a>
            {" "}
            and retry.
          </p>
        </div>
      </section>

      {/* ── AGENT LIBRARY ────────────────────────────────────────────── */}
      <section className="bg-white border border-slate-200 rounded-lg p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
            <Bot size={16} /> Agent Library — {agents.length} programmable AI SDR{agents.length === 1 ? "" : "s"}
          </h3>
          <button onClick={() => setShowNewAgent(true)}
                  disabled={!config?.elevenlabs_configured}
                  className="text-xs px-3 py-1.5 bg-amber-500 text-white rounded font-semibold hover:bg-amber-600 disabled:opacity-50 flex items-center gap-1"
                  data-testid="ge-new-agent">
            <Plus size={12} /> New agent
          </button>
        </div>
        {agents.length === 0 && (
          <div className="text-sm text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded">
            No agents yet — build your first AI SDR agent.
            Each agent is grounded in the Service KB (all 27 services already there).
          </div>
        )}
        {agents.length > 0 && (
          <div className="grid grid-cols-2 gap-3">
            {agents.map((a) => (
              <div key={a.id} className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`agent-row-${a.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-sm font-bold text-slate-900">{a.name}</div>
                    <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
                      <span className="uppercase tracking-wider">{a.region}</span>
                      <span>·</span>
                      <span>{a.language}</span>
                      <span>·</span>
                      <span>{a.llm}</span>
                    </div>
                  </div>
                  <button onClick={() => delAgent(a.id)} className="text-red-500 hover:text-red-700 text-xs">
                    <Trash2 size={12} />
                  </button>
                </div>
                <div className="text-[10px] font-mono text-slate-400 truncate">
                  EL agent · {a.elevenlabs_agent_id.slice(0, 24)}…
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── CALL HISTORY ────────────────────────────────────────────── */}
      <section className="bg-white border border-slate-200 rounded-lg p-5 space-y-3">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
          <Hash size={16} /> Call History — last {calls.length}
        </h3>
        {calls.length === 0 ? (
          <div className="text-sm text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded">
            No calls yet — trigger one from any prospect's drawer using the &quot;Call now&quot; button.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs">
                <tr className="text-left">
                  <th className="px-3 py-2 font-semibold">Started</th>
                  <th className="px-3 py-2 font-semibold">To</th>
                  <th className="px-3 py-2 font-semibold">Agent</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                  <th className="px-3 py-2 font-semibold">Outcome</th>
                  <th className="px-3 py-2 font-semibold">Duration</th>
                </tr>
              </thead>
              <tbody>
                {calls.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100 text-sm">
                    <td className="px-3 py-2 text-slate-500 text-xs whitespace-nowrap">
                      {c.started_at ? new Date(c.started_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-3 py-2 font-mono text-slate-700 text-xs">{c.to_number}</td>
                    <td className="px-3 py-2 text-xs">
                      {(agents.find((a) => a.id === c.agent_id)?.name) || "—"}
                    </td>
                    <td className="px-3 py-2">
                      <span className={"text-[10px] px-1.5 py-0.5 rounded font-bold uppercase " +
                                       (c.status === "completed" ? "bg-emerald-100 text-emerald-800" :
                                        c.status === "initiated" ? "bg-amber-100 text-amber-800" :
                                        "bg-slate-100 text-slate-700")}>
                        {c.status || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-700 max-w-xs truncate" title={c.outcome}>
                      {c.outcome_status ? <span className="font-semibold">{c.outcome_status}</span> : "—"}
                      {c.outcome && <div className="text-slate-500 truncate">{c.outcome}</div>}
                    </td>
                    <td className="px-3 py-2 text-slate-600 tabular-nums text-xs">
                      {c.duration_s ? `${c.duration_s}s` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {showNewAgent && (
        <NewAgentDialog
          onClose={() => setShowNewAgent(false)}
          onCreated={() => { setShowNewAgent(false); load(); }}
        />
      )}
    </div>
  );
}

function SetupRow({ ok, label, detail }) {
  return (
    <div className={"p-3 rounded border " + (ok ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50")}>
      <div className="flex items-center gap-2 text-sm font-semibold">
        {ok ? <Check size={14} className="text-emerald-700"/> : <X size={14} className="text-red-700"/>}
        {label}
      </div>
      <div className={"text-[11px] mt-0.5 " + (ok ? "text-emerald-700" : "text-red-700")}>{detail}</div>
    </div>
  );
}

function NewAgentDialog({ onClose, onCreated }) {
  const [f, setF] = useState({
    name: "", region: "na", language: "en", llm: "gemini-2.5-flash",
    voice_id: DEFAULT_VOICES[0].id, custom_voice_id: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Name is required");
    setSaving(true);
    const voice_id = f.custom_voice_id.trim() || f.voice_id;
    try {
      await api.post("/admin/growth/voice/agents", { ...f, voice_id });
      toast.success("Agent created");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40" data-testid="new-agent-dialog">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-2xl w-full max-w-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold flex items-center gap-2"><Bot size={18}/> New AI SDR Agent</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl">×</button>
        </div>
        <p className="text-xs text-slate-500">
          The system prompt is auto-generated from your Service KB (all 27 services).
          You can override it after creation via the ElevenLabs dashboard.
        </p>
        <TextInput label="Name *" value={f.name} onChange={(v) => setF({ ...f, name: v })}
                   placeholder="e.g. Aria · EN-US · Female" testid="new-agent-name"/>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Region</label>
            <select value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })}
                    className="w-full text-sm px-3 py-2 mt-1 border border-slate-200 rounded">
              {["na","uk","eu","in","apac","mena","latam"].map((r) => <option key={r} value={r}>{r.toUpperCase()}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Language</label>
            <select value={f.language} onChange={(e) => setF({ ...f, language: e.target.value })}
                    className="w-full text-sm px-3 py-2 mt-1 border border-slate-200 rounded">
              {["en","de","fr","es","hi","ja"].map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">LLM</label>
            <select value={f.llm} onChange={(e) => setF({ ...f, llm: e.target.value })}
                    className="w-full text-sm px-3 py-2 mt-1 border border-slate-200 rounded">
              <option value="gemini-2.5-flash">Gemini 2.5 Flash (fast)</option>
              <option value="gpt-4o">GPT-4o</option>
              <option value="claude-sonnet-4">Claude Sonnet 4</option>
            </select>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Voice</label>
          <select value={f.voice_id} onChange={(e) => setF({ ...f, voice_id: e.target.value })}
                  className="w-full text-sm px-3 py-2 mt-1 border border-slate-200 rounded">
            {DEFAULT_VOICES.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <input type="text" value={f.custom_voice_id} onChange={(e) => setF({ ...f, custom_voice_id: e.target.value })}
                 placeholder="OR paste a custom ElevenLabs voice ID (overrides dropdown)"
                 className="w-full text-xs font-mono px-3 py-1.5 mt-1.5 border border-slate-200 rounded"/>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t">
          <button type="button" onClick={onClose} className="text-sm px-4 py-2 text-slate-600 border border-slate-200 rounded">Cancel</button>
          <button type="submit" disabled={saving}
                  className="text-sm px-4 py-2 bg-amber-500 text-white rounded font-semibold hover:bg-amber-600 disabled:opacity-50"
                  data-testid="new-agent-save">
            {saving ? "Creating…" : "Create agent"}
          </button>
        </div>
      </form>
    </div>
  );
}
function TextInput({ label, value, onChange, placeholder = "", testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{label}</label>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
             data-testid={testid}
             className="w-full text-sm px-3 py-2 mt-1 border border-slate-200 rounded focus:outline-none focus:border-amber-500"/>
    </div>
  );
}
